import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { OutputType } from '@/tools/onnx/onnx.constants';
import { AnyProperty } from '@/utilities.types';
import { OnnxInputParameter } from '@/tools/onnx/onnx.utilities';

export const DEFAULT_DURATION = { value: 0, units: 'min' };

export class OnnxStore extends BaseToolStore {
  static readonly storeName = 'onnxStore';
  type = TREND_TOOLS.ONNX;
  parameterDefinitions = {
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
  };

  initialize() {
    this.state = this.immutable({
      ...BASE_TOOL_COMMON_PROPS,
      color: '',
      formulaItemId: null,
      onnxResultType: null,
      onnxInputParameters: [],
      outputType: OutputType.CONDITION,
      outputSignal: null,
      cleanCapsules: false,
      capsuleMinDuration: DEFAULT_DURATION,
      capsuleMergeDuration: DEFAULT_DURATION,
    });
  }

  get formulaItemId() {
    return this.state.get('formulaItemId');
  }

  get onnxResultType() {
    return this.state.get('onnxResultType');
  }

  get onnxInputParameters() {
    return this.state.get('onnxInputParameters');
  }

  get restrictToCondition() {
    return this.state.get('restrictToCondition');
  }

  get color(): string {
    return this.state.get('color');
  }

  get outputType() {
    return this.state.get('outputType');
  }

  get outputSignal() {
    return this.state.get('outputSignal');
  }

  get cleanCapsules(): boolean {
    return this.state.get('cleanCapsules');
  }

  get capsuleMinDuration() {
    return this.state.get('capsuleMinDuration');
  }

  get capsuleMergeDuration() {
    return this.state.get('capsuleMergeDuration');
  }

  rehydrate(dehydratedState: AnyProperty) {
    this.state.merge(dehydratedState);
  }

  setColor(payload: { color: string }) {
    this.state.set('color', payload.color);
  }

  setOutputType(payload: { outputType: OutputType }) {
    this.state.set('outputType', payload.outputType);
  }

  setFormulaItemId(payload: { formulaItemId: string }) {
    this.state.set('formulaItemId', payload.formulaItemId);
  }

  setOnnxResultType(payload: { onnxResultType: string }) {
    this.state.set('onnxResultType', payload.onnxResultType);
  }

  setOnnxInputParameters(payload: { parameters: OnnxInputParameter[] }) {
    this.state.set('onnxInputParameters', payload.parameters);
  }

  setCleanCapsules(payload: { cleanCapsules: boolean }) {
    this.state.set('cleanCapsules', payload.cleanCapsules);
  }

  setCapsuleMinDuration(payload: { capsuleMinDuration: number }) {
    this.state.set('capsuleMinDuration', payload.capsuleMinDuration);
  }

  setCapsuleMergeDuration(payload: { capsuleMergeDuration: number }) {
    this.state.set('capsuleMergeDuration', payload.capsuleMergeDuration);
  }

  updateOnnxInputParameter(payload: {
    index: number;
    parameter: { identifier: string; name: string; item: { id: string; name: string } };
  }) {
    this.state.set(['onnxInputParameters', payload.index], payload.parameter);
  }

  setOutputSignal(payload: { outputSignal: string }) {
    this.state.set('outputSignal', payload.outputSignal);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    ONNX_SET_COLOR: this.setColor,
    ONNX_SET_OUTPUT_TYPE: this.setOutputType,
    ONNX_SET_CLEAN_CAPSULES: this.setCleanCapsules,
    ONNX_SET_CAPSULE_MIN_DURATION: this.setCapsuleMinDuration,
    ONNX_SET_CAPSULE_MERGE_DURATION: this.setCapsuleMergeDuration,
    ONNX_SET_FORMULA_ITEM_ID: this.setFormulaItemId,
    ONNX_SET_ONNX_RESULT_TYPE: this.setOnnxResultType,
    ONNX_SET_INPUT_PARAMETERS: this.setOnnxInputParameters,
    ONNX_UPDATE_INPUT_PARAMETER: this.updateOnnxInputParameter,
    ONNX_SET_OUTPUT_SIGNAL: this.setOutputSignal,
  };
}
